import { z } from 'zod';
import { CLICK_ID_PARAMS } from '@superfunnel/types/pixel-website-event';

/**
 * Admin API version this integration is written against. Shopify releases
 * quarterly and supports each version for a year; bumping this is a deliberate
 * act because query shapes can change between versions.
 */
const SHOPIFY_API_VERSION = '2026-07';
/**
 * Scopes requested when a merchant installs the SuperFunnel app.
 *
 * Kept minimal on purpose -- Shopify's app review penalises asking for more
 * than a feature needs. `read_products` covers products, variants and
 * collections; `read_orders` is required to receive `orders/create` webhooks,
 * which is how a purchase becomes an attributed conversion. The two pixel
 * scopes let purchase tracking install our web pixel on the store.
 */
const SHOPIFY_PIXEL_SCOPES = [
    'write_pixels',
    'read_customer_events',
];
const SHOPIFY_SCOPES = [
    'read_products',
    'read_orders',
    ...SHOPIFY_PIXEL_SCOPES,
];
/**
 * A shop's permanent domain. Validated everywhere a shop domain crosses a
 * trust boundary: the OAuth callback and webhook payloads both carry one, and
 * both are attacker-controllable, so an unvalidated value would let a request
 * point our Admin API calls at an arbitrary host.
 */
const ShopifyShopDomainSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/, 'Expected a myshopify.com shop domain');
const ShopifyOAuthCallbackQuerySchema = z.object({
    code: z.string().min(1).nullish(),
    hmac: z.string().min(1).nullish(),
    shop: z.string().min(1).nullish(),
    state: z.string().min(1).nullish(),
    timestamp: z.string().nullish(),
    error: z.string().nullish(),
    error_description: z.string().nullish(),
});
/** Webhook topics the app subscribes to, and the handler each one drives. */
const shopifyWebhookTopics = [
    'products/create',
    'products/update',
    'products/delete',
    'collections/update',
    'orders/create',
    'app/uninstalled',
    'customers/data_request',
    'customers/redact',
    'shop/redact',
];
/**
 * Field ids of the synced products collection.
 *
 * These are stable, human-readable ids rather than generated ones: the mapper
 * writes entry values keyed by them, the agent binds templates to them, and the
 * route resolves `handle`. Generating them would make every sync depend on
 * reading the collection's schema first.
 */
/**
 * Field ids for the synced products collection.
 *
 * These are the CMS's own identifier form, not camelCase: `createCMSCollection`
 * puts every field id through `toCMSIdentifier`, which lowercases and
 * underscore-separates. Declaring them any other way means the collection is
 * created with ids the mapper then fails to write to, and every entry is
 * rejected as an unknown field.
 */
const SHOPIFY_PRODUCT_FIELD_IDS = {
    shopifyProductId: 'shopify_product_id',
    handle: 'handle',
    previousHandles: 'previous_handles',
    title: 'title',
    descriptionHtml: 'description_html',
    vendor: 'vendor',
    productType: 'product_type',
    category: 'category',
    tags: 'tags',
    collections: 'collections',
    priceMin: 'price_min',
    priceMax: 'price_max',
    compareAtPrice: 'compare_at_price',
    currencyCode: 'currency_code',
    available: 'available',
    requiresSellingPlan: 'requires_selling_plan',
    inSyncScope: 'in_sync_scope',
    checkoutUrl: 'checkout_url',
    featuredImage: 'featured_image',
    images: 'images',
    variants: 'variants',
};
/** Sub-field ids of the repeated `variants` group. */
const SHOPIFY_VARIANT_FIELD_IDS = {
    variantId: 'variant_id',
    title: 'title',
    price: 'price',
    compareAtPrice: 'compare_at_price',
    sku: 'sku',
    available: 'available',
    image: 'image',
    optionValues: 'option_values',
    checkoutUrl: 'checkout_url',
};
/**
 * How long a sync may go without a sign of life before it is treated as dead.
 *
 * Every slice of a run stamps `lastSyncProgressAt` when it starts, and a slice
 * lasts at most half an hour, so a live sync is never quiet this long.
 */
const SHOPIFY_SYNC_STALE_AFTER_MS = 60 * 60 * 1000;
/**
 * Whether a sync is still running, as opposed to having been killed part-way.
 *
 * The distinction matters because a collection that merely *looks* like it is
 * syncing is frozen: its resync button is disabled, its webhooks are skipped
 * and the reconcile leaves it alone. A sync cut off mid-write writes nothing to
 * say so, which is how one customer's catalog stayed stuck for four days -- so
 * silence, not status, is what this goes on.
 */
const isShopifySyncRunning = (source, now = Date.now()) => {
    if (source?.syncStatus !== 'syncing') {
        return false;
    }
    // Missing on a run that started before this field existed, which is exactly
    // the kind of run that got stuck.
    const progressAt = source.lastSyncProgressAt?.toMillis?.();
    return (progressAt !== undefined && now - progressAt < SHOPIFY_SYNC_STALE_AFTER_MS);
};
/**
 * Conversion event name a Shopify order is reported under.
 *
 * Shared so the reporter and the account-level registration cannot drift: an
 * event reported under a name the account does not list is invisible in the
 * experiment metric picker.
 */
const SHOPIFY_PURCHASE_EVENT_NAME = 'shopify-purchase';
/**
 * Cart attribute keys read off an order.
 *
 * Shopify persists a cart's `attributes[...]` onto the resulting order as note
 * attributes, which is how a purchase finds its way back to the session that
 * produced it. Nothing writes them yet -- stamping links is deferred to the
 * checkout CTA -- so today these are read from links a merchant built by hand.
 */
const SHOPIFY_CART_ATTRIBUTE_KEYS = {
    session: 'sf_session',
    campaign: 'sf_campaign',
    variant: 'sf_variant',
};
/**
 * Cart attribute name for one of the platform's click ids.
 *
 * Derived rather than listed so every network the platform already understands
 * -- Microsoft, TikTok, Reddit, LinkedIn and the rest of `CLICK_ID_PARAMS` --
 * is carried and read without this file having to know about it.
 */
const shopifyClickIdAttribute = (param) => `sf_${param}`;
const ShopifyConnectStoreBodySchema = z.object({
    accountId: z.string().min(1),
    shopDomain: ShopifyShopDomainSchema,
    from: z.string().nullish(),
});
/**
 * The query Shopify puts on the App URL when a merchant presses Install.
 *
 * `hmac` signs every other parameter, so the signature is verified against the
 * raw query; this validates the values the install then uses.
 */
const ShopifyInstallQuerySchema = z.object({
    shop: ShopifyShopDomainSchema,
    hmac: z.string().min(1),
    timestamp: z.string().min(1),
});
/**
 * Attach a store that installed itself from Shopify.
 *
 * The install arrives with no SuperFunnel account -- OAuth has to happen before
 * anything else, so there is nobody to attach it to yet -- and the token waits
 * under this claim until somebody signs in and takes it.
 */
const ShopifyClaimInstallBodySchema = z.object({
    accountId: z.string().min(1),
    claimId: z.string().min(1),
});
const ShopifyCreateSyncedCollectionBodySchema = z.object({
    accountId: z.string().min(1),
    connectedAccountId: z.string().min(1),
    name: z.string().min(1).max(120).nullish(),
    /** Shopify collection ids to pull. Empty syncs the whole catalog. */
    shopifyCollectionIds: z.array(z.string().min(1)).default([]),
});
const ShopifyListCollectionsBodySchema = z.object({
    accountId: z.string().min(1),
    connectedAccountId: z.string().min(1),
});
const ShopifyResyncCollectionBodySchema = z.object({
    accountId: z.string().min(1),
    collectionId: z.string().min(1),
    /** Replaces the stored sync scope when provided. */
    shopifyCollectionIds: z.array(z.string().min(1)).nullish(),
});
/**
 * Webhook payload shapes.
 *
 * Shopify sends these to an unauthenticated endpoint, so they are parsed rather
 * than cast. Every field is nullish because Shopify varies its payloads by topic
 * and API version; the handlers already treat a missing identifier as a no-op.
 * Unknown keys are stripped, which is what keeps a payload change from reaching
 * the mapper as an untyped value.
 */
const ShopifyWebhookIdSchema = z.union([z.number(), z.string()]).nullish();
const ShopifyProductWebhookPayloadSchema = z.object({
    id: ShopifyWebhookIdSchema,
    admin_graphql_api_id: z.string().nullish(),
    handle: z.string().nullish(),
});
const ShopifyCollectionWebhookPayloadSchema = z.object({
    id: ShopifyWebhookIdSchema,
    admin_graphql_api_id: z.string().nullish(),
});
const ShopifyNoteAttributeSchema = z.object({
    name: z.string().nullish(),
    value: z.string().nullish(),
});
const ShopifyOrderWebhookPayloadSchema = z.object({
    id: ShopifyWebhookIdSchema,
    admin_graphql_api_id: z.string().nullish(),
    name: z.string().nullish(),
    currency: z.string().nullish(),
    total_price: z.string().nullish(),
    created_at: z.string().nullish(),
    landing_site: z.string().nullish(),
    referring_site: z.string().nullish(),
    note_attributes: z.array(ShopifyNoteAttributeSchema).nullish(),
    line_items: z
        .array(z.object({
        title: z.string().nullish(),
        quantity: z.number().nullish(),
        price: z.string().nullish(),
        variant_id: ShopifyWebhookIdSchema,
        product_id: ShopifyWebhookIdSchema,
    }))
        .nullish(),
});
/** A support admin switching the purchase-tracking pixel for one store. */
const ShopifySetPurchaseTrackingBodySchema = z.object({
    accountId: z.string().min(1),
    connectedAccountId: z.string().min(1),
    enabled: z.boolean(),
});
const ShopifyApprovalLinkBodySchema = z.object({
    accountId: z.string().min(1),
    connectedAccountId: z.string().min(1),
});
const PixelTextSchema = z.string().trim().min(1).max(512).nullish();
/**
 * What our web pixel sends when a checkout completes.
 *
 * It comes from a shopper's browser, so nothing here is trusted beyond picking
 * the order and the session: the order's total is read back from the Admin
 * API rather than taken from this body.
 */
const ShopifyPixelPurchaseBodySchema = z.object({
    accountId: z.string().min(1).max(128),
    shopDomain: ShopifyShopDomainSchema,
    orderId: z.string().regex(/^gid:\/\/shopify\/Order\/\d+$/),
    touch: z.object({
        sfSession: z.string().regex(/^\d{1,20}$/),
        sfCampaign: PixelTextSchema,
        sfVariant: PixelTextSchema,
        clickIdType: z.enum(CLICK_ID_PARAMS).nullish(),
        clickId: PixelTextSchema,
        utmSource: PixelTextSchema,
        utmMedium: PixelTextSchema,
        utmCampaign: PixelTextSchema,
        utmContent: PixelTextSchema,
        utmTerm: PixelTextSchema,
        landingUrl: z.string().max(2048).nullish(),
        capturedAt: z.number().int().min(0),
    }),
});

export { SHOPIFY_API_VERSION, SHOPIFY_CART_ATTRIBUTE_KEYS, SHOPIFY_PIXEL_SCOPES, SHOPIFY_PRODUCT_FIELD_IDS, SHOPIFY_PURCHASE_EVENT_NAME, SHOPIFY_SCOPES, SHOPIFY_SYNC_STALE_AFTER_MS, SHOPIFY_VARIANT_FIELD_IDS, ShopifyApprovalLinkBodySchema, ShopifyClaimInstallBodySchema, ShopifyCollectionWebhookPayloadSchema, ShopifyConnectStoreBodySchema, ShopifyCreateSyncedCollectionBodySchema, ShopifyInstallQuerySchema, ShopifyListCollectionsBodySchema, ShopifyNoteAttributeSchema, ShopifyOAuthCallbackQuerySchema, ShopifyOrderWebhookPayloadSchema, ShopifyPixelPurchaseBodySchema, ShopifyProductWebhookPayloadSchema, ShopifyResyncCollectionBodySchema, ShopifySetPurchaseTrackingBodySchema, ShopifyShopDomainSchema, isShopifySyncRunning, shopifyClickIdAttribute, shopifyWebhookTopics };
//# sourceMappingURL=shopify.esm.js.map
